<template>
    <default-layout>
        <section class="flex min-h-98 h-screen">
            <section class="hidden md:inline-block min-w-98">
                <article class="space-y-2 p-5">
                    <div class="flex items-center justify-between">
                        <h1 class="font-bold text-xl text-gray-700">Chats</h1>
                        
                        <custom-dropdown placement="right">
                            <template v-slot:button>
                                <button class="flex rounded-md bg-transparent">
                                    <Icon icon="stash:new-window-page" class="text-3xl" />
                                </button>
                            </template>

                            <template v-slot:content>
                                <section>
                                    <custom-dropdown-item type="link" label="Directory" :to="{ name: 'contacts' }" />
                                    <custom-dropdown-item type="button" label="New Conversation" @click="() => isCreateChatOpen = true" />
                                </section>
                            </template>
                        </custom-dropdown>
                    </div>
                        
                    <input type="text" placeholder="Search messages or users" class="bg-gray-100 p-4 rounded-lg block w-full outline-none"/>
                </article>
                    
                <article>
                    <h2 class="font-bold my-2 px-5 text-gray-700">Recent</h2>
                    <article class="mx-2">
                        <custom-conversation-loading v-if="conversationLoading"/>

                        <custom-contact-card v-else v-for="chat in conversations"
                                        :key="chat.id"
                                        :id="chat.id"
                                        :photo="chat.contact?.photo"
                                        :name="chat.contact?.name"
                                        :message="chat.lastMessage"
                                        :date="chat.createdDate"
                                        :selected-id="conversation?.id"
                                        @click="onConnectToChat(chat)"
                                    />

                        <custom-empty-indicator 
                                :isEmpty="!conversations.length && !conversationLoading"
                                label="No chat available"
                        />
                    </article>
                </article>
            </section>

            <section class="bg-gray-50 w-full">
                <section v-if="conversation.id">
                    <article class="grid grid-cols-1 content-between h-screen min-h-98" >
                        <custom-chat-header
                                :guid="conversation?.contact?.guid"
                                :name="conversation?.contact?.name"
                                :phone="conversation?.contact?.phone"
                                :photo="conversation?.contact?.photo"
                            />
                            
                        <section data-simplebar-auto-hide="true"  class="h-full max-h-80vh overflow-auto" ref="chatContainer">
                            <section class="flex flex-col p-5">
                                <custom-chat-message 
                                                    v-for="(message, index) in conversation.messages" :key="index"
                                                    :recipient="conversation.contact?.phone"
                                                    :message="message"
                                                />
                            </section>
                        </section>

                        <article>
                            <form @submit.stop.prevent="sentMessage" class="flex items-center gap-4 p-5 border-t">
                                <div>
                                    <button>
                                        <Icon icon="ant-design:audio-outlined" />
                                    </button>
                                </div>

                                <div class="w-full">
                                    <input 
                                            type="text" 
                                            placeholder="Enter messages..." 
                                            class="p-2 rounded-lg w-full px-4 bg-white outline-none" 
                                            v-model="message.message"
                                            :disabled="isMessageSendLoading"   
                                        />
                                </div>

                                <div class="flex items-center gap-3">
                                    <custom-dropdown placement="right" :bottom="true">
                                        <template v-slot:button>
                                            <button type="button">
                                                <Icon icon="mingcute:emoji-line" />
                                            </button>
                                        </template>

                                        <template v-slot:content>
                                            <simplebar class="p-2 h-60">
                                                <div v-for="(icons, category) in emojis" :key="category">
                                                    <h3 class="text-sm font-semibold p-1">{{ category }}</h3>
                                                    <div class="flex flex-wrap justify-items-stretch justify-center m-auto gap-2 p-1 rounded-md">
                                                        <button type="button" v-for="emoji in icons" :key="emoji" class="text-sm" @click="onAddEmojiHandle(emoji)">
                                                            {{ emoji }}
                                                        </button>
                                                    </div>
                                                </div>
                                            </simplebar>
                                        </template>
                                    </custom-dropdown>
                                    <button type="button">
                                        <Icon icon="gridicons:attachment" />
                                    </button>
                                    <button type="submit" 
                                            :class="{
                                                'opacity-30': isMessageSendLoading,
                                            }"
                                            class="bg-secondary px-4 py-2 text-white rounded-lg"  
                                            :disabled="isMessageSendLoading">
                                            <Icon v-if="isMessageSendLoading" icon="line-md:loading-twotone-loop" />
                                            <Icon v-else icon="mingcute:send-line" />
                                    </button>
                                 </div>
                            </form>
                        </article>
                    </article>
                </section>

                <section v-else class="flex justify-items-center items-center h-full">
                    <custom-empty-indicator :isEmpty="!conversation.id" label="No chat available" />
                </section>
            </section>
        </section>

        <custom-modal 
                title="Conversation"
                subTitle="Send a proposal to your clients, remember that you cannot send direct messages to clients" 
                :is-open="isCreateChatOpen"
                @cancel="() => isCreateChatOpen = false">
                <template v-slot:body>
                    <form @submit.stop.prevent="onCreateHandle" class="grid grid-cols-2 gap-x-4 gap-y-3">
                        <div class="col-span-1">
                            <custom-select
                                    id="brandId"
                                    label="Brand"
                                    placeholder="Select a brand"
                                    :data="brands"
                                    v-model="beginConversation.idBrand"
                                    :setLabel="(model) => model.name"
                                    :setCode="(model) => model.id"
                                    :isLoading="isBrandLoading"
                                    :validate="validate$.idBrand"
                                    @change="() => getWhatsappConfigurations({
                                        idBrand: beginConversation.idBrand
                                    })"
                                />
                        </div>

                        <div class="col-span-1">
                            <custom-select
                                    id="whatsappAccountId"
                                    label="Phone"
                                    placeholder="Select a phone"
                                    :data="configurations"
                                    v-model="beginConversation.idChannelAccount"
                                    :setLabel="(model) => model.phone"
                                    :setCode="(model) => model.id"
                                    :isLoading="isConfigurationLoading"
                                    :validate="validate$.idChannelAccount"
                                />
                        </div>

                        <div class="col-span-full">
                            <custom-select
                                    id="contactId"
                                    placeholder="Select a contact"
                                    label="Contact"
                                    :data="contacts"
                                    v-model="beginConversation.idContact"
                                    :setLabel="(model) => model.name"
                                    :setCode="(model) => model.id"
                                    :isLoading="isContactLoading"
                                    :template="ContactSelectOption"
                                    :validate="validate$.idContact"
                                />
                        </div>

                        <div class="col-span-full">
                            <custom-select
                                    id="templateId"
                                    label="Template"
                                    placeholder="Select a template"
                                    :data="contacts"
                                    :setLabel="(model) => model.name"
                                    :isLoading="isContactLoading"
                                    :template="ContactSelectOption"
                                />
                        </div>

                        <div class="col-span-full">
                            <div class="float-right">
                                <custom-button 
                                            label="Create" 
                                            type="submit"
                                            size="xs" 
                                            :isLoading="isCreateLoading" />
                            </div>
                        </div>
                    </form>
                </template>
        </custom-modal>
    </default-layout>
</template>
<script setup>
import { ref, onMounted, onUnmounted, reactive, nextTick, watch } from 'vue'
import { Icon } from "@iconify/vue";
import { useAccountStore } from '@/core/store/accountStore';
import { useConversation } from '@/core/composables/useConversation'
import { HubConnectionBuilder  } from "@microsoft/signalr";
import { Create } from "@/infrastructure/repositories/messageRepository"
import { toast, variant } from '@/core/helper/utility';
import { useContact } from '@/core/composables/useContact';
import { useBrand } from '@/core/composables/useBrand'
import { useWhatsappConfiguration } from '@/core/composables/useWhatsappConfiguration'
import simplebar from 'simplebar-vue';
import DefaultLayout from '../layouts/DefaultLayout.vue';
import CustomContactCard from '../components/CustomContactCard.vue';
import CustomChatHeader from '../components/CustomChatHeader.vue';
import CustomChatMessage from "../components/CustomChatMessage.vue";
import CustomConversationLoading from '../components/loading/CustomConversationLoading.vue';
import CustomDropdown from '@/app/components/CustomDropdown'
import CustomButton from '@/app/components/CustomButton'
import CustomModal from '@/app/components/CustomModal'
import CustomSelect from '@/app/components/CustomSelect'
import ContactSelectOption from '@/app/components/options/ContactSelectOption'
import CustomDropdownItem from '@/app/components/CustomDropdownItem'
import CustomEmptyIndicator from '@/app/components/CustomEmptyIndicator.vue';

const chatContainer = ref(null);
const messageHub = ref(null);

const {
    validate$,
    isCreateLoading,
    isCreateChatOpen,
    conversations,
    conversation,
    beginConversation,
    conversationLoading,
    getConversations,
    getMessages,
    onCreateHandle
} = useConversation();

const {
    isLoading: isContactLoading,
    contacts,
    getContacts
} = useContact();

const {
    isLoading: isBrandLoading,
    brands,
    getBrands
} = useBrand();

const {
    isLoading: isConfigurationLoading,
    configurations,
    getWhatsappConfigurations
} = useWhatsappConfiguration();

var isMessageSendLoading = ref(false);
var message = reactive({
    to: null,
    type: null,
    idConversation: null,
    message: null
});

const accountStore = useAccountStore();

const onConnectToChat = async (chat) => {
    try {
        conversation.value = chat;
        await getMessages(chat?.id);

    } catch (error) {
        toast({
            variant: variant.error,
            title: 'Chat',
            body: 'Could not connect to chat, check again, please contact support center'
        });

        throw new Error(error);
    }
}

const setupConnection = async () => {
    try {
        messageHub.value = new HubConnectionBuilder()
                            .withUrl(`${process.env.VUE_APP_URL_CORE_HUB}/chathub`, {
                                accessTokenFactory: () => accountStore.getAccessToken,
                            })
                            .build();

        messageHub.value.on("ReceiveMessage", message => {
            let index = conversations.value.findIndex(c => c.id == message.idConversation);

            if(index >= 0){
                let existingMessage = conversations.value[index]?.messages?.find((m) => m.uId == message.uId);
                if(existingMessage){
                    Object.assign(existingMessage, { ...message });
                    return;
                }
                
                conversations.value[index].lastMessage = message;
                conversations.value[index].messages.push(message);
            }
        });

        messageHub.value.onclose(() => { });

        messageHub.value.onreconnected(() => { });

        await messageHub.value
                        .start()
                        .then(() => {})
                        .catch((err) => console.error(err));
    } catch (error) {
        toast({
            variant: variant.error,
            title: 'Chat',
            body: 'Could not connect to chat, check again, please contact support center'
        });

        throw new Error(error);
    }
}

const sentMessage = async () => {
    try {
        isMessageSendLoading.value = true;

        var response = await Create({
            ...message,
            type: 'text',
            to: conversation.value.contact.phone,
            idConversation: conversation.value.id,
        });

        if(response.status == 200){
            message = {
                to: null,
                type: null,
                idConversation: null,
                message: null
            };
        }else{
            toast({
                collection: true,
                variant: variant.error,
                title: response.data.title,
                body:  response.data.messages
            });
        }

        isMessageSendLoading.value = false;
    } catch (error) {
        isMessageSendLoading.value = false;
        toast({
            variant: variant.error,
            title: 'Message',
            body: 'Could not send message, check again, please contact support center'
        });

        throw new Error(error);
    }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    }
  });
};

watch(() => conversation.value.messages, scrollToBottom, { deep: true });

onMounted(async () => {
    await getConversations();
    await setupConnection();
    await getContacts();
    await getBrands();
});

onUnmounted(async () => {
    if(messageHub.value){
        await messageHub.value.stop();
    }
});

const emojis = ref({
  Faces: [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', 
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', 
    '🤩', '🥳', '😎', '🤓', '😏', '😒', '😞', '😔', '😟', '😕', 
    '🙁', '☹️', '🥺', '😢', '😭', '🤯', '😠', '😡', '🤬', '🤔', 
    '🤨', '😐', '😑', '😶'
  ],
  Hands: [
    '👋', '🤚', '🖐', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', 
    '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', 
    '✊', '👊', '🤛', '🤜', '👏', '🙌', '🤝'
  ],
  Hearts: ['❤️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '❣️', '💔', '💌'],
  Animals: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🦝', '🐻', '🐼', '🦁', '🐮', '🐷', '🐸', '🐵', '🦄', '🐢', '🦜'],
  Fruits: ['🍏', '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍒', '🍍', '🥥', '🥝', '🥑', '🍆', '🥕', '🌽'],
  Food: ['🍔', '🍟', '🌭', '🍕', '🍜', '🍣', '🍩', '🍪', '🎂', '🍿'],
  Drinks: ['☕', '🥤', '🍺'],
  Celebrations: ['🎉', '🎊', '🎈', '🎁', '🏆', '🏅'],
  Entertainment: ['🎤', '🎧', '🎭', '🎨', '🎮'],
  Transport: ['🚗', '🚕', '🚙', '🚌', '🚎', '🚓', '🚑', '🚒', '🚲', '🏍️', '🚀', '✈️', '🚢'],
  Nature: ['🌍', '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌪️', '🌈', '🌊', '🏔️', '🏕️', '🌋', '🏝️'],
  Tech: ['📱', '💻', '🖥️', '⌨️', '🖱️', '🖲️', '🕹️']
});

const onAddEmojiHandle = (emoji) => {
    message.message = message.message ? message.message + emoji : emoji;
};
</script>